import { mediator } from '../index.js'

class AndroidConnector {
  constructor(dispatch, signalize) {
    this.dispatch = dispatch
    this.signalize = signalize
    this.name = 'android'
    this.connected = false
    this.state = {
      stream: null,
      peer: null,
      devices: {},
    }
    this.config = {
      address: 'ws://localhost:8887',
    }
  }


  async getStream() {
    const device = this.getMaximumCapabilityDevice()
    this.stream = await this.createPeer(device)
  }

  async getTrack(device) {
    try {
      await this.prepare(device)
      const track = this.stream.getTracks().filter((track) => track.kind === device)[0]
      return track.clone()
    } catch (error) {
      console.error(error)
    }
  }

  //
  // WebSocket methods
  //

  async connect() {
    return new Promise((resolve, reject) => {
      this.ws = new window.WebSocket(this.config.address)
      this.ws.onopen = () => {
        this.getDevices()
      }
      this.ws.onerror = () => {
        resolve(false)
      }
      this.ws.onclose = () => {
        resolve(false)
      }
      this.ws.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.method === 'enumerateDevices') {
          this.enumerateDevicesMessageHandler(data)
          this.ws.onmessage = this.onMessage
          this.connected = true
          resolve(true)
        }
      }
    })
  }

  notify(event) {
  }

  send = (message) => {
    this.ws.send(JSON.stringify(message))
  }

  onMessage = (event) => {
    const data = JSON.parse(event.data)
    const handlers = {
      enumerateDevices: this.enumerateDevicesMessageHandler,
      'p2p:answer': this.onAnswer,
      'p2p:candidate': this.onCandidate,
    }
    if (handlers[data.method]) handlers[data.method](data)
  }

  //
  // WebSocket message handlers
  //

  enumerateDevicesMessageHandler = (data) => {
    this.devices = data.devices
    this.setDevicePriority(this.devices)
    // this.setDevices(this.devices) 
  }

  onAnswer = (data) => {
    const { sdp } = data.params
    this.peer.setRemoteDescription(new window.RTCSessionDescription({ type: 'answer', sdp })).catch(console.error)
  }

  onCandidate = (data) => {
    const candidate = data.params
    this.peer.addIceCandidate(new window.RTCIceCandidate(candidate)).catch(console.error)
  }

  //
  // WebRTC methods
  //

  createPeer(device) {
    return new Promise((resolve, reject) => {
      const iceServers = mediator.session?.configuration?.iceServers
      const pc = new window.RTCPeerConnection({
        ...(iceServers ? { iceServers } : {}),
        iceCandidatePoolSize: 5,
        bundlePolicy: 'max-compat',
        rtcpMuxPolicy: 'negotiate', // Options: 'require', 'negotiate'
        iceTransportPolicy: 'all', //Options: 'all', 'relay'
      })
      pc.onicecandidate = (event) => {
        if (event.candidate) this.handlePeerCandidateSignal(device.deviceId, event.candidate.toJSON())
      }
      pc.ontrack = (event) => resolve(event.streams[0])
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') reject(new Error('Peer connection failed'))
      }
      this.peer = pc
      // Receive-only transceivers: without them the offer contains no media sections at all,
      // so the Android device can never attach its camera/microphone tracks to the answer
      // and ontrack never fires (regression from the multi-peer Peer.js implementation,
      // which called addTransceiver('audio') / addTransceiver('video') before offering).
      pc.addTransceiver('audio', { direction: 'recvonly' })
      pc.addTransceiver('video', { direction: 'recvonly' })
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer).then(() => offer))
        .then((offer) => this.handlePeerOfferSignal(device.deviceId, offer.sdp))
        .catch(reject)
    })
  }

  handlePeerCandidateSignal = (peerName, candidate) => {
    candidate.sdp = candidate.candidate
    delete candidate.candidate
    this.send(this.p2pCandidateMessage(peerName, candidate))
  }

  handlePeerOfferSignal = (peerName, sdp) => {
    this.send(this.p2pOfferMessage(peerName, sdp))
  }

  //
  // Custom methods
  //

  async prepare(device) {
    if (this.checkWebsocketConnection()) {
      if (this.hasDevice(device)) {
        if (!this.stream) {
          await this.getStream()
        }
      } else {
        console.error('DEVICE NOT FOUND')
        throw new Error('Device not found')
      }
    } else {
      console.error('WEBSOCKET NOT READY')
      throw new Error('Websocket is disconnected')
    }
  }

  checkWebsocketConnection = () => {
    if (this.ws.readyState === window.WebSocket.OPEN) {
      return true
    } else {
      return false
    }
  }

  hasDevice = (deviceType) => {
    const device = this.devices.filter((device) => device.kind === deviceType === 'video' ? 'videoinput' : 'audioinput')[0]
    return device || false
  }

  getDevices() {
    this.send(this.enumerateDevicesMessage())
  }

  getMaximumCapabilityDevice() {
    const microphone = (device) => device.kind === 'audioinput'
    const camera = (device) => device.kind === 'videoinput'
    const videoinput = this.devices.filter(camera)[0]
    const audioinput = this.devices.filter(microphone)[0]
    return videoinput || audioinput
  }

  setDevicePriority(devices) {
    return this.devices.filter((device) => {
      if (device.kind === 'videoinput') {
        device.priority = 1
      }
    })
  }

  //
  // Messages
  //

  enumerateDevicesMessage = () => {
    return {
      method: 'enumerateDevices',
    }
  }

  p2pOfferMessage = (device, sdp) => {
    return {
      method: 'p2p:offer',
      params: { device, sdp },
    }
  }

  p2pCandidateMessage = (device, candidate) => {
    return {
      method: 'p2p:candidate',
      params: {
        ...candidate,
        device,
      },
    }
  }
}
const androidConnector = new AndroidConnector()
export { androidConnector }
